/**
 * FILE-OVERVIEW:
 * - 役割: スケジュールデータの取得・再取得・保持を担当。
 * - 主な連携: services/calendarApi.js, components/Calendar/CalendarMain.js
 * - 本番運用上の注意: 再取得間隔やフィルタ順序を変更すると表示体感が変わる。
 */

import { useState, useEffect } from 'react';
import { loadCalendarEvents } from '../services/calendarApi';
import type { CalendarEvent } from '../services/calendarApi';
import type { AuthUser } from './useAuth';
import { asErrorLike } from '../utils/errorLike';

/**
 * スケジュール一覧を取得・保持し、必要に応じて自動更新するカスタムフック。
 *
 * @param {Object|null} currentUser ログインユーザー情報（認証切替時の再取得トリガとして利用）
 * @param {boolean} autoUpdate true の場合、一定間隔で再取得を行う
 * @returns {{events: Array, setEvents: Function}} 画面表示用イベント配列と更新関数
 *
 * 本番運用上の注意:
 * - 更新間隔を短くしすぎると API 負荷が増えるため運用要件に合わせて調整する。
 */
export const useCalendarEvents = (currentUser: AuthUser | null, autoUpdate: boolean) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // 初回スケジュール取得（auth 解決を待たずに即発火し初期表示を高速化する）
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const loadedEvents = await loadCalendarEvents();
        setEvents(Array.isArray(loadedEvents) ? loadedEvents : []);
      } catch (error) {
        console.error('スケジュール取得エラー:', error);
        setEvents([]);
        // message が無い値が投げられても catch 内で落ちないようにする
        if ((asErrorLike(error).message ?? '').includes('認証')) {
          alert('認証エラーが発生しました。ログインしてください。');
        }
      }
    };
    fetchEvents();
  }, [currentUser]);

  // 自動更新
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    if (autoUpdate) {
      intervalId = setInterval(async () => {
        try {
          const loadedEvents = await loadCalendarEvents();
          setEvents(Array.isArray(loadedEvents) ? loadedEvents : []);
        } catch (error) {
          console.error('自動更新中にエラー:', error);
        }
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoUpdate]);

  return {
    events,
    setEvents
  };
};